import React, {useEffect, useState} from "react";
import WebFont from 'webfontloader';
import Select from "react-select";
import {tryToParse} from "../../../utils/arrayObjectHelper";
import {Button, Input as AntInput} from "antd";
import {Modal} from "@nutastic/nutastic-react-app";

const componentClass = 'fontPicker';

const fontLibrary = {
    Montserrat: {
        url: 'Montserrat:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900',
        family: '"Montserrat", sans-serif',
        sizes: [
            { label: 'Thin', value: 300 },
            { label: 'Default', value: 400 },
            { label: 'Bold', value: 700 },
            { label: 'Extra Thick', value: 900 }
        ],
        styles: [
            { label: 'Normal', value: 'normal'},
            { label: 'Italicize', value: 'italic'}
        ]
    },
    SourceSansPro: {
        url: 'Source+Sans+3:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300;1,400;1,700;1,900',
        family: '"Source Sans 3", sans-serif',
        sizes: [
            { label: 'Thin', value: 300 },
            { label: 'Default', value: 400 },
            { label: 'Bold', value: 700 },
            { label: 'Extra Thick', value: 900 }
        ],
        styles: [
            { label: 'Normal', value: 'normal'},
            { label: 'Italicize', value: 'italic'}
        ]
    },
    Pacifico: {
        url: 'Pacifico',
        family: '"Pacifico", cursive',
    }
};

const getFontLibraryOptions = keyList => (keyList || []).filter(k => fontLibrary.hasOwnProperty(k)).map(key => {
    return {
        label: key,
        value: key
    };
});

const fontLibraryOptionGroups = [
    { label: 'Standard', options: getFontLibraryOptions(['Montserrat', 'SourceSansPro']) },
    { label: 'Creative', options: getFontLibraryOptions(['Pacifico']) }
];

const defaultWeightOpt = { label: 'Default', value: 400 };
const defaultSizeOpt = { label: 'Default', value: 'default'};
const defaultStyleOpt = { label: 'Normal', value: 'normal'};
const commonSizeOptions = [
    { label: 'XS', value: 'xs'},
    { label: 'S', value: 's'},
    defaultSizeOpt,
    { label: 'L', value: 'l'},
    { label: 'XL', value: 'xl'},
];
const commonSpacingOptions = [
    { label: 'Least', value: 'xs'},
    { label: 'Less', value: 's'},
    defaultSizeOpt,
    { label: 'More', value: 'l'},
    { label: 'Most', value: 'xl'},
];
const selectClassNames = { control: () => "dropSelect-control" };

const FontPicker = ({ defaultFont = 'Montserrat', onChange, value, showSizeFields, showSpacingFields, useStringify }) => {
    const [showModal, setShowModal] = useState(false);
    const valueToUse = value && useStringify ? tryToParse(value, {}) : value;

    const onSelectChange = (k, v) => {
        const nuValue = { ...valueToUse, [k]: v };
        onChange(useStringify ? JSON.stringify(nuValue) : nuValue);
    }

    const loadFont = fontKeyToLoad => {
        WebFont.load({
            google: {
                families: [fontLibrary[fontKeyToLoad]?.url]
            }
        });
    };

    useEffect(() => {
        if (valueToUse?.fontKey) {
            loadFont(valueToUse?.fontKey?.value);
        }
    }, []);

    const getPreviewFontStyle = () => {
        const style = {};
        const fontConfig = fontLibrary?.[valueToUse?.fontKey?.value || defaultFont];
        style['fontFamily'] = fontConfig.family;
        style['fontWeight'] = valueToUse.fontWeight?.value || 400;
        style['fontStyle'] = valueToUse.fontStyle?.value || 'normal';
        return style;
    }

    const renderFontPickerFields = () => {
        return <div className={`${componentClass}__fields`}>
            <span className={`${componentClass}__exampleText`} style={getPreviewFontStyle()}>This is a preview of what your font looks like...</span>
            <div className={`${componentClass}__largeCol`}>
                <label>Font Family</label>
                <Select
                    classNames={selectClassNames}
                    options={fontLibraryOptionGroups}
                    onChange={(v) => {
                        const nuValue = {
                            ...valueToUse,
                            fontKey: v,
                            fontWeight: defaultWeightOpt,
                            fontStyle: defaultStyleOpt
                        };
                        onChange(useStringify ? JSON.stringify(nuValue) : nuValue);
                        loadFont(v?.value);
                    }}
                    value={valueToUse?.fontKey || { label: defaultFont, value: defaultFont }}
                    name="fontKey"
                />
            </div>
            { showSizeFields && <div className={`${componentClass}__smallCol`}>
                <label>Font Size</label>
                <Select
                    classNames={selectClassNames}
                    options={commonSizeOptions}
                    onChange={v => onSelectChange('fontSize', v)}
                    value={ valueToUse?.fontSize || defaultSizeOpt }
                    name="fontSize"
                />
            </div> }
            { showSizeFields && <div className={`${componentClass}__smallCol`}>
                <label>Font Weight</label>
                <Select
                    classNames={selectClassNames}
                    options={ fontLibrary?.[valueToUse?.fontKey?.value || defaultFont]?.sizes || [defaultWeightOpt] }
                    onChange={v => onSelectChange('fontWeight', v)}
                    value={valueToUse?.fontWeight || defaultWeightOpt}
                    name="fontWeight"
                />
            </div> }
            { showSizeFields && <div className={`${componentClass}__smallCol`}>
                <label>Font Style</label>
                <Select
                    classNames={selectClassNames}
                    options={ fontLibrary?.[valueToUse?.fontKey?.value || defaultFont]?.styles || [defaultStyleOpt] }
                    onChange={v => onSelectChange('fontStyle', v)}
                    value={valueToUse?.fontStyle || defaultStyleOpt}
                    name="fontStyle"
                />
            </div> }
            { showSpacingFields && <div className={`${componentClass}__col`}>
                <label>Line Spacing</label>
                <Select
                    classNames={selectClassNames}
                    options={ commonSpacingOptions }
                    onChange={v => onSelectChange('lineHeight', v)}
                    value={valueToUse?.lineHeight || defaultSizeOpt}
                    name="lineHeight"
                />
            </div> }
            { showSpacingFields && <div className={`${componentClass}__col`}>
                <label>Letter Spacing</label>
                <Select
                    classNames={selectClassNames}
                    options={ commonSpacingOptions }
                    onChange={v => onSelectChange('letterSpacing', v)}
                    value={valueToUse?.letterSpacing || defaultSizeOpt}
                    name="letterSpacing"
                />
            </div> }
        </div>
    }

    return <React.Fragment>
        <div className={componentClass}>
            <button className={`${componentClass}__button btn`} onClick={() => setShowModal(true)}>
                <i className="fa fa-edit" /><span>Edit Font</span>
            </button>
            <AntInput className={`${componentClass}__fontDisplay`} disabled={true} value={valueToUse?.fontKey?.value || defaultFont} />
        </div>
        <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            className={`${componentClass}-Modal`}
            title="Change Your Font Style"
            footer={null}
        >
            { showModal && renderFontPickerFields() }
            <div className={`${componentClass}__actions`}>
                <Button type={"primary"} shape={"round"} className={`${componentClass}__saveButton`} onClick={() => setShowModal(false)}>Done</Button>
            </div>
        </Modal>
    </React.Fragment> ;
};

export default FontPicker;
